'use client';

import { useEffect, useMemo, useState } from "react";
import Container from "@/components/Container";
import Card from "@/components/Card";
import Button from "@/components/Button";
import { apiGet, apiPost } from "@/lib/api";
import Logo from "@/components/Logo";

type House = { id: number; name: string; location: string; description?: string };
type Quote = { house_id: number; nights: number; nightly_rate: number; total_amount: number };
type Booking = { id: number; total_amount: number };

const inputClass =
  "mt-2 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-[#1a1c18] shadow-sm outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200";

export default function BookPage() {
  const [houses, setHouses] = useState<House[]>([]);
  const [houseId, setHouseId] = useState<number | "">("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<House[]>("/houses").then(setHouses).catch(() => setHouses([]));
  }, []);

  const canQuote = useMemo(() => !!houseId && !!checkIn && !!checkOut, [houseId, checkIn, checkOut]);

  async function onQuote() {
    setError(null);
    setQuote(null);
    if (!canQuote) return;
    try {
      const q = await apiPost<Quote>("/pricing/quote", {
        house_id: houseId,
        check_in: checkIn,
        check_out: checkOut,
      });
      setQuote(q);
    } catch {
      setError("Could not calculate price. Check dates and try again.");
    }
  }

  async function onCreateBooking() {
    setError(null);
    if (!quote) {
      setError("Please calculate price first.");
      return;
    }
    setLoading(true);
    try {
      const b = await apiPost<Booking>("/bookings", {
        house_id: houseId,
        guest_name: guestName,
        guest_email: guestEmail,
        guest_phone: guestPhone,
        check_in: checkIn,
        check_out: checkOut,
      });
      window.location.href = `/checkout/${b.id}`;
    } catch {
      setError("Booking failed. Please verify your details.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container className="space-y-10">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <Logo />
        <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-emerald-800">
          <span className="rounded-full bg-emerald-100 px-3 py-1">Step 1</span>
          Guest details
          <span className="h-1 w-6 rounded-full bg-emerald-200" />
          Dates
          <span className="h-1 w-6 rounded-full bg-emerald-200" />
          Payment
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-6">
          <Card title="Guest details" eyebrow="Start here">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-[#2f322c]">Full name</label>
                <input
                  className={inputClass}
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2f322c]">Phone</label>
                <input
                  className={inputClass}
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  placeholder="+1 555..."
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-[#2f322c]">Email</label>
                <input
                  className={inputClass}
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="jane@example.com"
                />
              </div>
            </div>
          </Card>

          <Card title="House and dates" eyebrow="Choose a stay">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-[#2f322c]">Select house</label>
                <select
                  className={inputClass}
                  value={houseId}
                  onChange={(e) => setHouseId(e.target.value ? Number(e.target.value) : "")}
                >
                  <option value="">Choose a house...</option>
                  {houses.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name} - {h.location}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-[#6a6f66]">
                  Need a different location? Our team can add houses anytime.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-[#2f322c]">Check-in</label>
                <input
                  type="date"
                  className={inputClass}
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2f322c]">Check-out</label>
                <input
                  type="date"
                  className={inputClass}
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <Button type="button" variant="secondary" onClick={onQuote} disabled={!canQuote}>
                Calculate price
              </Button>

              {quote && (
                <div className="rounded-xl bg-[#f7f2ea] px-4 py-3 text-sm text-[#1a1c18] ring-1 ring-black/5">
                  <div className="flex flex-wrap gap-x-6 gap-y-1">
                    <span>
                      <b>Nights:</b> {quote.nights}
                    </span>
                    <span>
                      <b>Rate:</b> ${quote.nightly_rate}/night
                    </span>
                    <span>
                      <b>Total:</b> ${quote.total_amount}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
          </Card>
        </div>

        <div className="space-y-6">
          <div className="rounded-[28px] bg-[#1a2a22] p-7 text-[#f7f2ea] shadow-[0_35px_80px_-60px_rgba(16,24,40,0.9)]">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#cfe8d9]">Booking summary</p>
            <h2 className="mt-3 text-2xl font-serif">Your stay overview</h2>
            <div className="mt-4 space-y-2 text-sm text-[#d7e4db]">
              <p>House: {houses.find((h) => h.id === houseId)?.name || "Select a house"}</p>
              <p>Dates: {checkIn && checkOut ? `${checkIn} to ${checkOut}` : "Choose dates"}</p>
              <p>Guest: {guestName || "Your full name"}</p>
            </div>
            <div className="mt-6 rounded-2xl bg-white/10 px-4 py-3 text-sm">
              <p className="text-[#f7f2ea]">Total due</p>
              <p className="mt-1 text-2xl font-semibold">
                {quote ? `$${quote.total_amount}` : "$0"}
              </p>
            </div>
          </div>

          <div className="rounded-2xl bg-white/90 p-6 ring-1 ring-black/5">
            <p className="text-sm font-semibold text-[#1a1c18]">What happens next?</p>
            <ul className="mt-3 space-y-2 text-sm text-[#5b5f57]">
              <li>1. We create your booking and reserve the dates.</li>
              <li>2. You choose a payment method and confirm.</li>
              <li>3. We send confirmation and arrival details.</li>
            </ul>
          </div>

          <Button type="button" onClick={onCreateBooking} disabled={loading || !quote} className="w-full">
            {loading ? "Creating booking..." : "Continue to payment"}
          </Button>

          <div className="text-sm">
            <a className="text-emerald-700 hover:underline" href="/">{"<- Back to home"}</a>
          </div>
        </div>
      </div>
    </Container>
  );
}
